import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';

// Profile data interface
interface ProfileData {
  id: number;
  name: string;
  avatarUrl?: string | null;
  avatarInitials?: string | null;
  description?: string | null;
  [key: string]: any; // Allow for additional properties
}

type CareRecipientViewContextType = {
  isInCareRecipientView: boolean;
  hubId: window.__currentHubId
  profileData: ProfileData | null;
  setIsInCareRecipientView: (value: boolean) => void;
  setProfileId: (id: number | null) => void;
  exitCareRecipientView: () => void;
};

const CareRecipientViewContext = createContext<CareRecipientViewContextType>({
  isInCareRecipientView: false,
  hubId: window.__currentHubId,
  profileData: null,
  setIsInCareRecipientView: () => {},
  setProfileId: () => {},
  exitCareRecipientView: () => {},
});

export const useCareRecipientView = () => useContext(CareRecipientViewContext);

export const CareRecipientViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInCareRecipientView, setIsInCareRecipientView] = useState(false);
  const [profileId, setProfileId] = useState<number | null>(null);
  const [location] = useLocation();

  // Automatically detect if we're in Care Recipient View based on the route or parameters
  useEffect(() => {
    // Parse URL and query parameters more accurately
    
    // Check if URL matches any care recipient view pattern
    const isCareRecipientViewRoute = location.startsWith('/care-recipient') || 
                                    location === '/care-recipient-view' || 
                                    location === '/care-recipient-visual-timers';
                                    
    console.log("Care Recipient Context - Checking route:", location, 
                "Is care recipient route:", isCareRecipientViewRoute);
    
    // Check if URL has careRecipientView=true parameter
    // Use URLSearchParams to properly parse query parameters
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const hasExplicitParameter = params.get('careRecipientView') === 'true';
    
    // Also check for view-only mode parameter which indicates care recipient view
    const isViewOnlyMode = params.get('mode') === 'view-only';
    
    // Check for query parameter in the URL string as fallback
    const hasParameterInUrl = location.includes('careRecipientView=true');
    
    // Determine view status from all sources
    const hasParameter = hasExplicitParameter || hasParameterInUrl || isViewOnlyMode;
    const isInCRView = isCareRecipientViewRoute || hasParameter;
    
    console.log("Care Recipient View Context - Detected view status:", { 
      isInCRView, 
      isCareRecipientViewRoute, 
      hasParameter,
      hasExplicitParameter,
      isViewOnlyMode,
      hasParameterInUrl,
      params: Array.from(params.entries()),
      location,
      href: window.location.href
    });
    
    // If we're in view-only mode on the /timer page, navigate to the passive display
    if (isViewOnlyMode && location.startsWith('/timer')) {
      console.log("🔄 Auto-redirecting from view-only timer to passive display");
      const profileIdParam = params.get('profileId');
      const redirectUrl = `/care-recipient-visual-timers${profileIdParam ? `?profileId=${profileIdParam}` : ''}`;
      
      // Use history API to redirect
      window.location.href = redirectUrl;
      return;
    }
    
    setIsInCareRecipientView(isInCRView);
    
    // Extract profileId from various sources
    if (isCareRecipientViewRoute) {
      // From URL path for direct care recipient view
      const careRecipientMatch = location.match(/\/care-recipient\/(\d+)/);
      const careRecipientViewMatch = location.match(/\/care-recipient-view\/(\d+)/);
      
      if (careRecipientMatch && careRecipientMatch[1]) {
        const id = parseInt(careRecipientMatch[1], 10);
        console.log(`Setting profileId to ${id} from care-recipient URL path`);
        setProfileId(id);
      } else if (careRecipientViewMatch && careRecipientViewMatch[1]) {
        const id = parseInt(careRecipientViewMatch[1], 10);
        console.log(`Setting profileId to ${id} from care-recipient-view URL path`);
        setProfileId(id);
      } else {
        // If we're on a care recipient page but no ID in URL, try to get from params
        const profileIdParam = params.get('profileId');
        if (profileIdParam && !isNaN(Number(profileIdParam))) {
          const id = parseInt(profileIdParam, 10);
          console.log(`Setting profileId to ${id} from URL query parameter on care recipient page`);
          setProfileId(id);
        }
      }
    } else if (hasParameter) {
      // Try to extract profileId from query parameters first
      const profileIdParam = params.get('profileId');
      if (profileIdParam && !isNaN(Number(profileIdParam))) {
        const id = parseInt(profileIdParam, 10);
        console.log(`Setting profileId to ${id} from URL query parameter`);
        setProfileId(id);
      } else {
        // Fallback to regex match
        const match = location.match(/profileId=(\d+)/);
        if (match && match[1]) {
          const id = parseInt(match[1], 10);
          console.log(`Setting profileId to ${id} from URL string match`);
          setProfileId(id);
        }
      }
    }
  }, [location]);

  // Fetch profile data when profileId is available
  const { data: profileData } = useQuery<{ hubId: window.__currentHubId, profile: ProfileData } | ProfileData>({
    queryKey: ['/api/managed-profiles', profileId],
    queryFn: () => fetch(`/api/managed-profiles/${profileId}`).then(res => res.json()),
    enabled: !!profileId && isInCareRecipientView,
  });

  // Extract the profile data whether it's direct or nested
  const profile = 'profile' in (profileData || {}) 
    ? profileData?.profile 
    : profileData as ProfileData || null;
  
  console.log("Care Recipient View Context - Profile data:", { profileData, profile });

  // Function to completely exit Care Recipient View and reset all state
  const exitCareRecipientView = () => {
    console.log("Exiting Care Recipient View and clearing all state");
    setIsInCareRecipientView(false);
    setProfileId(null);
  };

  return (
    <CareRecipientViewContext.Provider
      value={{
        isInCareRecipientView,
        profileId,
        profileData: profile,
        setIsInCareRecipientView,
        setProfileId,
        exitCareRecipientView,
      }}
    >
      {children}
    </CareRecipientViewContext.Provider>
  );
};